import { AbstractSecurityStorage, AuthModule, LogLevel } from 'angular-auth-oidc-client';
import { DxcIconModule, DxcIconService } from '@dxc/ui-components/icon';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  faArrowLeft,
  faArrowUpRightFromSquare,
  faArrowUp,
  faArrowDown,
  faArrowsRotate,
  faArrowUpArrowDown,
  faBallotCheck,
  faBars,
  faBolt,
  faBook,
  faBuildingShield,
  faCalendarCheck,
  faCalendarDays,
  faCalendarPlus,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faCircleBolt,
  faCircleCheck,
  faCircleDown,
  faCircleExclamation,
  faCircleInfo,
  faCircleQuestion,
  faCircleXmark,
  faClock,
  faClone,
  faCopy,
  faDollarSign,
  faDownload,
  faEye,
  faEyeSlash,
  faFileLines,
  faGear,
  faHeadset,
  faInfo,
  faLightbulb,
  faList,
  faNotebook,
  faPenToSquare,
  faPhone,
  faPhonePlus,
  faQuestion,
  faSliders,
  faSpinner,
  faTrashCan,
  faUser,
  faUserDoctor,
  faUserGear,
  faUserPen,
  faInboxArrowUp,
  faBoxArchive
} from '@fortawesome/pro-solid-svg-icons';
import {
  faBoltAuto,
  faCircleInfo as farCircleInfo,
  faComment as farComment,
  faPen as farPen,
  faShieldCheck as farShieldCheck,
  faStickyNote as farStickyNote
} from '@fortawesome/pro-regular-svg-icons';
import { faPen, faRotateRight, faShieldCheck } from '@fortawesome/pro-solid-svg-icons';

import { AppConstants } from './constants/app.constants';
import { AppContainerComponent } from './components/app-container/app-container.component';
import { AuthTokenInterceptor } from './interceptors/auth-token.interceptor';
import { BreadcrumbModule } from 'xng-breadcrumb';
import { CommonModule } from '@angular/common';
import { DownloadEligibilityFormComponent } from './components/download-eligibility-form/download-eligibility-form.component';
import { DxcBadgeModule } from '@dxc/ui-components/badge';
import { DxcButtonModule } from '@dxc/ui-components/button';
import { DxcCardModule } from '@dxc/ui-components/card';
import { DxcCheckboxModule } from '@dxc/ui-components/checkbox';
import { DxcCollapseModule } from '@dxc/ui-components/collapse';
import { DxcDateRangePickerComponent } from '@dxc/ui-components/datepicker';
import { DxcDatepickerModule } from '@dxc/ui-components/datepicker';
import { DxcDropdownModule } from '@dxc/ui-components/dropdown';
import { DxcEmptyModule } from '@dxc/ui-components/empty';
import { DxcFormModule } from '@dxc/ui-components/form';
import { DxcLoaderModule } from '@dxc/ui-components/loader';
import { DxcMenuModule } from '@dxc/ui-components/menu';
import { DxcModalModule } from '@dxc/ui-components/modal';
import { DxcAlertModule } from '@dxc/ui-components/alert';
import { DxcPaginationModule } from '@dxc/ui-components/pagination';
import { DxcProgressModule } from '@dxc/ui-components/progress';
import { DxcScrollSpyModule } from '@dxc/ui-components/scroll-spy';
import { DxcSearchModule } from '@dxc/ui-components/search';
import { DxcSelectModule } from '@dxc/ui-components/select';
import { DxcSideNavModule } from '@dxc/ui-components/side-nav';
import { DxcSwitchModule } from '@dxc/ui-components/switch';
import { DxcTabNavModule } from '@dxc/ui-components/tab-nav';
import { DxcTableModule } from '@dxc/ui-components/table';
import { DxcTimelineModule } from '@dxc/ui-components/timeline';
import { DxcTimepickerModule } from '@dxc/ui-components/timepicker';
import { DxcToastModule } from '@dxc/ui-components/toast';
import { DxcTooltipModule } from '@dxc/ui-components/tooltip';
import { DxcTypographyModule } from '@dxc/ui-components/typography';
import { EligibilityCompleteViewerComponent } from './components/eligibility-complete-viewer/eligibility-complete-viewer.component';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpCacheInterceptor } from './interceptors/cache.interceptor';
import { HttpErrorInterceptor } from './interceptors/error.interceptor';
import { LocalStorageService } from './services/storage/storage.service';
import { MarkdownModule } from 'ngx-markdown';
import { NewEligibilityFormComponent } from './components/new-eligibility-form/new-eligibility-form.component';
import { NgModule } from '@angular/core';
import { NgbCarouselModule } from '@ng-bootstrap/ng-bootstrap';
import { RedirectComponent } from './components/redirect/redirect.component';
import { RouterModule } from '@angular/router';
import { SharedModule } from '@app/shared/shared.module';
import { UrlConstants } from './constants/url.constants';
import { UserIdleModule } from 'angular-user-idle';
import { environment } from '../../environments/environment';
import { faArrowsRetweet } from '@fortawesome/pro-duotone-svg-icons';
import { CheckEligibilityComponent } from './components/check-eligibility/check-eligibility.component';

@NgModule({
  declarations: [
    AppContainerComponent,
    RedirectComponent,
    NewEligibilityFormComponent,
    DownloadEligibilityFormComponent,
    EligibilityCompleteViewerComponent,
    CheckEligibilityComponent
  ],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    AuthModule.forRoot({
      config: {
        authority: `${environment.oauth2PublicEndpoint}`,
        authWellknownEndpointUrl: `${environment.oauth2PublicEndpoint}${UrlConstants.authWellknownEndpointUrl}`,
        clientId: environment.oauth2ClientId,
        redirectUrl: `${window.location.origin}${UrlConstants.authCallback}`,
        postLogoutRedirectUri: `${window.location.origin}${UrlConstants.authCallback}`,
        postLoginRoute: UrlConstants.loginRedirectUrl,
        forbiddenRoute: UrlConstants.loginUrl,
        scope: 'openid offline_access consent_skip user_profile user_address groups features vip',
        customParamsAuthRequest: { audience: environment.oauthAudience },
        customParamsRefreshTokenRequest: {
          audience: environment.oauthAudience
        },
        responseType: 'code',
        silentRenew: true,
        useRefreshToken: true,
        ignoreNonceAfterRefresh: true,
        renewTimeBeforeTokenExpiresInSeconds: 30,
        logLevel: environment.production ? LogLevel.Error : LogLevel.Debug,
        maxIdTokenIatOffsetAllowedInSeconds: AppConstants.maxIatOffset
      }
    }),
    UserIdleModule,
    DxcSideNavModule,
    DxcIconModule,
    DxcTooltipModule,
    DxcDropdownModule,
    SharedModule,
    DxcLoaderModule,
    DxcModalModule,
    DxcDatepickerModule,
    DxcSelectModule,
    DxcFormModule,
    DxcProgressModule,
    DxcSearchModule,
    DxcToastModule,
    DxcEmptyModule,
    DxcTimepickerModule,
    DxcCheckboxModule,
    NgbCarouselModule,

    BreadcrumbModule,
    DxcTabNavModule,
    DxcTypographyModule,
    DxcTableModule,
    DxcPaginationModule,
    DxcButtonModule,
    DxcBadgeModule,
    DxcCardModule,
    DxcScrollSpyModule,
    DxcCollapseModule,
    DxcMenuModule,
    DxcTimelineModule,
    DxcSwitchModule,
    DxcAlertModule,
    MarkdownModule.forRoot()
  ],
  exports: [
    DxcToastModule,
    DxcEmptyModule,
    NewEligibilityFormComponent,
    DownloadEligibilityFormComponent,
    DxcModalModule,
    EligibilityCompleteViewerComponent,
    DxcDateRangePickerComponent,
    CheckEligibilityComponent
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthTokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpCacheInterceptor,
      multi: true
    },
    {
      provide: AbstractSecurityStorage,
      useClass: LocalStorageService
    }
  ]
})
export class CoreModule {
  constructor(private dxcIconService: DxcIconService) {
    this.dxcIconService.addIcon(
      faShieldCheck,
      faPen,

      // Solid
      faLightbulb,
      faPhone,
      faBook,
      faBars,
      faArrowsRotate,
      faCircleCheck,
      faCircleXmark,
      faCircleQuestion,
      faCircleExclamation,
      faClock,
      faUser,
      faBuildingShield,
      faDollarSign,
      faNotebook,
      faArrowLeft,
      faFileLines,
      faSliders,
      faUserPen,
      faCalendarPlus,
      faEyeSlash,
      faEye,
      faPenToSquare,
      faTrashCan,
      faChevronRight,
      faChevronLeft,
      faInfo,
      faCircleInfo,
      faSpinner,
      faCopy,
      faCircleDown,
      faQuestion,
      faUserDoctor,
      faCalendarDays,
      faClone,
      faRotateRight,
      faHeadset,
      faCircleBolt,
      faDownload,
      faUserGear,
      faGear,
      faBallotCheck,
      faPhonePlus,
      faCheck,
      faList,
      faArrowUpArrowDown,
      faArrowUp,
      faArrowDown,
      faArrowUpRightFromSquare,
      //regular
      farShieldCheck,
      farPen,
      farComment,
      farStickyNote,
      faCalendarCheck,
      faBoltAuto,
      faBolt,
      farCircleInfo,

      //Duotone
      faArrowsRetweet,
      faInboxArrowUp,
      faBoxArchive
    );
  }
}
